import { State, StateAction } from '@sierralabs/state-machine';
import { DynamicFormContextState } from '../../DynamicFormContext';
import { TransitionConditionPlugin } from '../TransitionConditionPlugin';
import { User } from '../../../models/User';
import { differenceInYears, isAfter, isBefore, isSameDay } from 'date-fns';

export enum UserDataFieldConditionOperator {
  Equal = 'equal',
  NotEqual = 'not-equal',
  HasValue = 'has-value',
  ContainsValue = 'contains-value',
  GreaterThan = 'greater-than',
  LessThan = 'less-than',
  After = 'after',
  Before = 'before'
}

export interface UserDataFieldCondition {
  fieldKey: string;
  operator: UserDataFieldConditionOperator;
  values?: (string | number | boolean | Date)[];
}

export const UserDataFieldConditionPlugin: TransitionConditionPlugin<UserDataFieldCondition> =
  {
    type: 'User Data Field Condition',
    validate: (condition: Partial<StateAction<UserDataFieldCondition>>) => {
      return true;
    },
    guard: (
      state: State<DynamicFormContextState<any, any>>,
      params: UserDataFieldCondition
    ) => {
      const value = {
        ...state.context.user,
        birthDate: new Date(state.context.user.birthDate),
        age: differenceInYears(
          new Date(),
          new Date(`${state.context.user['birthDate' as keyof User]}`)
        )
      }[params.fieldKey as keyof User] as any;
      switch (params?.operator) {
        case UserDataFieldConditionOperator.HasValue:
          if (params.values && params.values.length > 0) {
            // check if at lease X number of items selected
            return Array.isArray(value) && value.length >= +params.values;
          } else if (Array.isArray(value)) {
            return value.length > 0;
          } else {
            return typeof value !== 'undefined';
          }
        case UserDataFieldConditionOperator.ContainsValue: {
          const values = Array.isArray(value) ? value : [value];
          const paramValues = params.values || [];
          return paramValues.every(pv => values.some(value => value === pv));
        }
        case UserDataFieldConditionOperator.NotEqual: {
          const values = Array.isArray(value) ? value : [value];
          if (values.length === 0) return false;
          for (const value of values) {
            for (const paramValue of params.values || []) {
              if (params.fieldKey === 'birthDate') {
                if (isSameDay(new Date(paramValue as any), value)) {
                  return false;
                }
              } else {
                // using non-strict equality
                if (paramValue == value) {
                  return false;
                }
              }
            }
          }
          return true;
        }
        case UserDataFieldConditionOperator.GreaterThan:
          return value > +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.LessThan:
          return value < +(params.values?.[0] || 0);
        case UserDataFieldConditionOperator.After:
          return isAfter(value, new Date(params.values?.[0] as any));
        case UserDataFieldConditionOperator.Before:
          return isBefore(value, new Date(params.values?.[0] as any));
        default: {
          const values = Array.isArray(value) ? value : [value];
          if (values.length === 0) return false;
          for (const value of values) {
            for (const paramValue of params.values || []) {
              if (params.fieldKey === 'birthDate') {
                if (!isSameDay(new Date(paramValue as any), value)) {
                  return false;
                }
              } else {
                // using non-strict equality
                if (paramValue != value) {
                  return false;
                }
              }
            }
          }
          return true;
        }
      }
    }
  };
